import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter le token + la concession globale à chaque requête
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Concession globale choisie par un super-admin / téléopérateur via le menu utilisateur
    // (sélecteur sidebar → localStorage 'crm-global-concession'). Ignorée par le backend
    // pour les autres rôles (cf. applyEffectiveConcessionFilter).
    const globalConcession = localStorage.getItem('crm-global-concession');
    if (globalConcession) {
      config.headers['X-Concession-Id'] = globalConcession;
    }
    // Plaque active choisie dans le menu (super-admin, 2026-10-26). Ignorée par
    // le backend pour les autres rôles, qui ont la plaque de leur compte.
    // Seul Dashboard écrit ces deux clés (sélecteurs du menu).
    const plaque = localStorage.getItem('crm-plaque');
    if (plaque) {
      config.headers['X-Plaque-Id'] = plaque;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs d'authentification et IP
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403 && error.response?.data?.error === 'Accès refusé depuis cette adresse IP') {
      window.dispatchEvent(new CustomEvent('ip-blocked'));
      return new Promise(() => {}); // suspend la chaîne pour éviter les erreurs en cascade
    }
    if (error.response?.status === 401) {
      // Aperçu expiré (jeton de 2 h) : on rend au super-admin son propre compte
      // au lieu de le déconnecter.
      const tokenAvantApercu = localStorage.getItem('token_avant_apercu');
      if (tokenAvantApercu) {
        localStorage.setItem('token', tokenAvantApercu);
        localStorage.removeItem('token_avant_apercu');
        localStorage.removeItem('user');
        window.location.href = '/';
        return new Promise(() => {});
      }
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Authentification
export const auth = {
  login: (username, password) => api.post('/auth/login', { username, password }),
  getConfig: () => api.get('/auth/config'),
  microsoftStart: () => api.get('/auth/microsoft/start'),
  microsoftCallback: (code, state) => api.post('/auth/microsoft/callback', { code, state }),
  register: (userData) => api.post('/auth/register', userData),
  getMe: () => api.get('/auth/me'),
  // Super-admin : jeton lecture seule pour voir le CRM comme un autre compte.
  apercu: (userId) => api.post(`/auth/apercu/${userId}`),
  changePassword: (currentPassword, newPassword) => api.put('/auth/change-password', { currentPassword, newPassword }),
  setup2fa: (tempToken) => api.get('/auth/2fa/setup', { headers: { Authorization: `Bearer ${tempToken}` } }),
  enable2fa: (tempToken, code) => api.post('/auth/2fa/enable', { code }, { headers: { Authorization: `Bearer ${tempToken}` } }),
  verify2fa: (tempToken, code) => api.post('/auth/verify-2fa', { temp_token: tempToken, code }, { headers: { Authorization: `Bearer ${tempToken}` } }),
};

// Demandes
export const demandes = {
  getAll: (params) => api.get('/demandes', { params }),
  getById: (id) => api.get(`/demandes/${id}`),
  create: (data) => api.post('/demandes', data),
  update: (id, data) => api.put(`/demandes/${id}`, data),
  archive: (id, motif) => api.post(`/demandes/${id}/archive`, { motif }),
  unarchive: (archiveId) => api.post(`/demandes/archives/${archiveId}/unarchive`),
  delete: (id) => api.delete(`/demandes/${id}`),
  getCorbeille: () => api.get('/demandes/corbeille'),
  restaurer: (corbeilleId) => api.post(`/demandes/corbeille/${corbeilleId}/restaurer`),
  getArchives: (params) => api.get('/demandes/archives', { params }),
  getStats: () => api.get('/demandes/stats'),
  getMyCount: (userId) => api.get('/demandes/my-count', userId ? { params: { user_id: userId } } : undefined),
  getDashboardStats: (params) => api.get('/demandes/dashboard-stats', params ? { params } : undefined),
  getSuiviCollaborateurs: (params) => api.get('/demandes/suivi-collaborateurs', params ? { params } : undefined),
  getCommentaires: (id) => {
    return api.get(`/demandes/${id}/commentaires`);
  },
  addCommentaire: (id, commentaire) => {
    return api.post(`/demandes/${id}/commentaires`, { commentaire });
  },
  updateCommentaire: (demandeId, commentaireId, commentaire) => {
    return api.put(`/demandes/${demandeId}/commentaires/${commentaireId}`, { commentaire });
  },
  importDemandes: (rows) => api.post('/demandes/import', { rows }),
};

// Parking
export const parking = {
  getMyCount: (userId) => api.get('/parking/my-count', userId ? { params: { user_id: userId } } : undefined),
};

// Tour Manager
export const tourManager = {
  getMyCount:  (userId) => api.get('/tour-manager/mes-taches/count', userId ? { params: { user_id: userId } } : undefined),
  updateStatut: (id, statut) => api.put(`/tour-manager/${id}/statut`, { statut }),
  deleteTache:  (id) => api.delete(`/tour-manager/${id}`),
  createTache:  (data) => api.post('/tour-manager/crm', data),
};

// Satisfaction (ex-Retours Prospects) — formulaires configurables, réponses,
// traitement. L'ancien préfixe /retours-prospects ne sert plus que la webapp
// publique, dont l'URL ne doit pas changer.
export const satisfaction = {
  getCatalogue:    ()          => api.get('/satisfaction/catalogue'),
  getFormulaires:  (params)    => api.get('/satisfaction/formulaires', { params }),
  getFormulaire:   (id)        => api.get(`/satisfaction/formulaires/${id}`),
  createFormulaire:(data)      => api.post('/satisfaction/formulaires', data),
  updateFormulaire:(id, data)  => api.put(`/satisfaction/formulaires/${id}`, data),
  dupliquer:       (id)        => api.post(`/satisfaction/formulaires/${id}/dupliquer`),
  deleteFormulaire:(id)        => api.delete(`/satisfaction/formulaires/${id}`),
  apercu:          (id, data)  => api.post(`/satisfaction/formulaires/${id}/apercu`, data),
  simuler:         (id)        => api.post(`/satisfaction/formulaires/${id}/simuler`),
  getEnvois:       (params)    => api.get('/satisfaction/envois', { params }),
  getStats:        (params)    => api.get('/satisfaction/stats', { params }),
  getSites:        (params)    => api.get('/satisfaction/sites', { params }),
  tableauBord:     (params)    => api.get('/satisfaction/tableau-bord', { params }),
  getEnvoi:        (id)        => api.get(`/satisfaction/envois/${id}`),
  createEnvoi:     (data)      => api.post('/satisfaction/envois', data),
  deleteEnvoi:     (id)        => api.delete(`/satisfaction/envois/${id}`),
  deleteEnvois:    (ids)       => api.post('/satisfaction/envois/supprimer', { ids }),
  brouillon:       (id, data)  => api.post(`/satisfaction/envois/${id}/brouillon`, data),
  traiter:         (id, data)  => api.post(`/satisfaction/envois/${id}/traitements`, data),
  annulerTraitement:(id)       => api.delete(`/satisfaction/envois/${id}/traitement`),
  getProjets:      (params)    => api.get('/satisfaction/projets', { params }),
  getCompteurs:    ()          => api.get('/satisfaction/compteurs'),
  suivreProjet:    (id, data)  => api.put(`/satisfaction/envois/${id}/projet`, data),
  getChartes:      (params)    => api.get('/satisfaction/chartes', { params }),
  saveCharte:      (data)      => api.put('/satisfaction/chartes', data),
  deleteCharte:    (id)        => api.delete(`/satisfaction/chartes/${id}`),
};

// Réception VIP
export const receptionVip = {
  getCount: () => api.get('/reception-vip/count'),
};

// OR en cours
export const orEnCours = {
  getAll:             (params) => api.get('/or-en-cours', { params }),
  getById:            (id)     => api.get(`/or-en-cours/${id}`),
  sync:               ()       => api.post('/or-en-cours/sync'),
  getStats:           ()       => api.get('/or-en-cours/stats'),
  getDashboard:       ()       => api.get('/or-en-cours/dashboard'),
  getReceptionnairesDistincts: () => api.get('/or-en-cours/receptionnaires-distincts'),
  getMappings:        ()       => api.get('/or-en-cours/receptionnaire-mapping'),
  createMapping:      (data)   => api.post('/or-en-cours/receptionnaire-mapping', data),
  deleteMapping:      (alias)  => api.delete(`/or-en-cours/receptionnaire-mapping/${encodeURIComponent(alias)}`),
  updateStatut:       (id, data) => api.put(`/or-en-cours/${id}/statut`, data),
  getCommentaires:    (id)     => api.get(`/or-en-cours/${id}/commentaires`),
  addCommentaire:     (id, contenu) => api.post(`/or-en-cours/${id}/commentaires`, { contenu }),
  deleteCommentaire:  (id, cid) => api.delete(`/or-en-cours/${id}/commentaires/${cid}`),
  exportAll:          (params)  => api.get('/or-en-cours/export', { params }),
};

// Garantie
export const garantie = {
  getAll:             (params) => api.get('/garantie', { params }),
  exportXLSX:         (params) => api.get('/garantie/export', { params, responseType: 'blob' }),
  getById:            (id)     => api.get(`/garantie/${id}`),
  sync:               ()       => api.post('/garantie/sync'),
  syncSigar:          ()       => api.post('/garantie/sync-sigar'),
  getStats:           ()       => api.get('/garantie/stats'),
  getDashboard:       (params) => api.get('/garantie/dashboard', { params }),
  getPilotage:        (params) => api.get('/garantie/pilotage', { params }),
  getRapproComptable: (params) => api.get('/garantie/rappro-comptable', { params }),
  actualiserHonda:    ()       => api.post('/garantie/releves/actualiser-honda'),
  actualiserFord:     (formData) => api.post('/garantie/actualiser-ford', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  bulkUpdate:         (ids, data) => api.post('/garantie/bulk-update', { ids, data }),
  getSuiviCollaborateurs: (params) => api.get('/garantie/suivi-collaborateurs', { params }),
  updateStatut:       (id, data) => api.put(`/garantie/${id}/statut`, data),
  getCommentaires:    (id)     => api.get(`/garantie/${id}/commentaires`),
  addCommentaire:     (id, contenu) => api.post(`/garantie/${id}/commentaires`, { contenu }),
  deleteCommentaire:  (id, cid) => api.delete(`/garantie/${id}/commentaires/${cid}`),
  getDocuments:       (id)     => api.get(`/garantie/${id}/documents`),
  presignUpload:      (id, data) => api.post(`/garantie/${id}/documents/presign`, data),
  confirmUpload:      (id, data) => api.post(`/garantie/${id}/documents/confirm`, data),
  deleteDocument:     (id, docId) => api.delete(`/garantie/${id}/documents/${docId}`),
  getLogs:            (id)     => api.get(`/garantie/${id}/logs`),
  getReleves:         ()       => api.get('/garantie/releves'),
  presignReleve:      (data)   => api.post('/garantie/releves/presign', data),
  confirmReleve:      (data)   => api.post('/garantie/releves/confirm', data),
  deleteReleve:       (id)     => api.delete(`/garantie/releves/${id}`),
  updateReleve:       (id, data) => api.patch(`/garantie/releves/${id}`, data),
  importReleve:       (id)     => api.post(`/garantie/releves/${id}/import`),
  clotureReleve:      (id)     => api.post(`/garantie/releves/${id}/cloture`),
  rouvrirReleve:      (id)     => api.delete(`/garantie/releves/${id}/cloture`),
  saveLignes:         (id, lignes) => api.post(`/garantie/releves/${id}/lignes`, { lignes }),
  getLignes:          ()       => api.get('/garantie/releves/lignes'),
  deleteLigne:        (id)     => api.delete(`/garantie/releves/lignes/${id}`),
  updateLigne:        (id, data) => api.patch(`/garantie/releves/lignes/${id}`, data),
  deleteLignesBulk:   (ids)    => api.delete('/garantie/releves/lignes', { data: { ids } }),
  recalculerStatutsLignes: ()  => api.post('/garantie/releves/lignes/recalculer-statuts'),
  deleteLignesByReleve: (releveId) => api.delete(`/garantie/releves/${releveId}/lignes`),
  suggestOr:          (vin)    => api.get('/garantie/releves/suggest-or', { params: { vin } }),
  checkClaims:        (items, marque) => api.post('/garantie/releves/check-claims', { items, marque }),
};

// Paramètres
export const settings = {
  getSftp: () => api.get('/settings/sftp'),
  updateSftp: (data) => api.put('/settings/sftp', data),
};

// Synchronisation automatique
export const syncSchedules = {
  getAll:  ()           => api.get('/sync-schedules'),
  update:  (key, data)  => api.put(`/sync-schedules/${key}`, data),
  run:     (key)        => api.post(`/sync-schedules/${key}/run`),
  getLogs: ()           => api.get('/sync-schedules/logs'),
};

// Webapp Inventaire — zones de parking par concession
export const parkingZones = {
  list:   (concessionId) => api.get('/parking-zones', { params: concessionId ? { concession_id: concessionId } : {} }),
  listNoms: (concessionId) => api.get('/parking-zones', { params: { sans_image: '1', ...(concessionId ? { concession_id: concessionId } : {}) } }),
  create: (data) => api.post('/parking-zones', data),
  update: (id, data) => api.put(`/parking-zones/${id}`, data),
  remove: (id) => api.delete(`/parking-zones/${id}`),
};

// Webapp Inventaire — accès utilisateurs
export const webappAccess = {
  getModules:  () => api.get('/webapp/modules'),
  getAccess:   () => api.get('/webapp/access'),
  saveAccess:  (userId, access, allowedZones) => api.put(`/webapp/access/${userId}`, { access, allowed_zones: allowedZones || [] }),
  clearAccess: (userId) => api.delete(`/webapp/access/${userId}`),
};

// Utilisateurs
export const users = {
  getAll: () => api.get('/users'),
  getResponsables: () => api.get('/users/responsables'),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  resetPassword: (id, newPassword) => api.put(`/users/${id}/reset-password`, { newPassword }),
};

// Gestion Commerciale — dossiers commerciaux VN/VO
export const dossiersCommerciaux = {
  // Préremplissage : on dépose le bon de commande, on récupère les champs.
  // Aucun dossier n'est créé — le vendeur relit avant d'enregistrer.
  extraire: (fichier) => {
    const fd = new FormData();
    fd.append('fichier', fichier);
    // ⚠ Il faut EFFACER Content-Type, pas le poser ni l'omettre.
    // L'instance axios impose `application/json` à toutes les requêtes (en tête
    // de ce fichier) : omettre l'en-tête laisse donc partir du JSON et le
    // serveur ne voit aucun fichier. Le poser à `multipart/form-data` à la main
    // est pire — l'en-tête part alors SANS la frontière (`boundary=…`) qui
    // sépare les parties, et le serveur rend une erreur HTML.
    // Mis à `undefined`, axios le recalcule depuis le FormData, frontière
    // comprise. Les deux essais précédents ont échoué chacun d'une des deux
    // façons ci-dessus.
    return api.post('/dossiers-commerciaux/extraire', fd, {
      headers: { 'Content-Type': undefined },
    });
  },
  // Sociétés sélectionnables (codsoc) pour un dossier : c'est ce qui permet à
  // A97 BI de ventiler les bons de commande au grain des ventes.
  societes: () => api.get('/dossiers-commerciaux/societes'),
  getAll: (params) => api.get('/dossiers-commerciaux', { params }),
  getById: (id) => api.get(`/dossiers-commerciaux/${id}`),
  create: (data) => api.post('/dossiers-commerciaux', data),
  update: (id, data) => api.put(`/dossiers-commerciaux/${id}`, data),
  updateStatut: (id, data) => api.put(`/dossiers-commerciaux/${id}/statut`, data),
  delete: (id) => api.delete(`/dossiers-commerciaux/${id}`),
  getStats: (params) => api.get('/dossiers-commerciaux/stats/summary', { params }),
  getVendeurs: () => api.get('/dossiers-commerciaux/vendeurs'),
  // ⚠ Pour la CRÉATION : `/vendeurs` ne rend que ceux déjà présents sur un
  // dossier (filtre de la liste), donc rien tant qu'aucun n'est désigné.
  getVendeursAssignables: () => api.get('/dossiers-commerciaux/vendeurs-assignables'),
  // Planning livraisons
  getPlanning: (date) => api.get('/dossiers-commerciaux/planning', { params: { date } }),
  updateLivraison: (id, data) => api.put(`/dossiers-commerciaux/${id}/livraison`, data),
  // Documents
  getDocuments: (id) => api.get(`/dossiers-commerciaux/${id}/documents`),
  getDocumentSlots: (id) => api.get(`/dossiers-commerciaux/${id}/documents/slots`),
  presignDocument: (id, data) => api.post(`/dossiers-commerciaux/${id}/documents/presign`, data),
  confirmDocument: (id, data) => api.post(`/dossiers-commerciaux/${id}/documents/confirm`, data),
  validateDocument: (id, docId) => api.put(`/dossiers-commerciaux/${id}/documents/${docId}/validate`),
  refuseDocument: (id, docId, data) => api.put(`/dossiers-commerciaux/${id}/documents/${docId}/refuse`, data),
  deleteDocument: (id, docId) => api.delete(`/dossiers-commerciaux/${id}/documents/${docId}`),
  analyzeDocument: (id, docId) => api.post(`/dossiers-commerciaux/${id}/documents/${docId}/analyze`),
  autoSplitAnalyze: (id, data) => api.post(`/dossiers-commerciaux/${id}/documents/auto-split/analyze`, data),
  splitPdf: (id, data) => api.post(`/dossiers-commerciaux/${id}/documents/split`, data),
  mergePdf: (id, data) => api.post(`/dossiers-commerciaux/${id}/documents/merge`, data),
  // Extras
  getExtras: (id) => api.get(`/dossiers-commerciaux/${id}/extras`),
  addExtra: (id, data) => api.post(`/dossiers-commerciaux/${id}/extras`, data),
  updateExtra: (id, extraId, data) => api.put(`/dossiers-commerciaux/${id}/extras/${extraId}`, data),
  deleteExtra: (id, extraId) => api.delete(`/dossiers-commerciaux/${id}/extras/${extraId}`),
  getApports: (id) => api.get(`/dossiers-commerciaux/${id}/apports`),
  addApport: (id, data) => api.post(`/dossiers-commerciaux/${id}/apports`, data),
  deleteApport: (id, apportId) => api.delete(`/dossiers-commerciaux/${id}/apports/${apportId}`),
  // Messages
  getMessages: (id) => api.get(`/dossiers-commerciaux/${id}/messages`),
  sendMessage: (id, data) => api.post(`/dossiers-commerciaux/${id}/messages`, data),
  presignMessage: (id, data) => api.post(`/dossiers-commerciaux/${id}/messages/presign`, data),
  confirmMessage: (id, data) => api.post(`/dossiers-commerciaux/${id}/messages/confirm-attachment`, data),

  // Portail client : le jeton n'est renvoyé qu'à la génération, jamais relu.
  lienClient: (id) => api.get(`/dossiers-commerciaux/${id}/lien-client`),
  genererLienClient: (id) => api.post(`/dossiers-commerciaux/${id}/lien-client`),
  // Historique
  getHistorique: (id) => api.get(`/dossiers-commerciaux/${id}/historique`),
  // Config (admin)
  getTypesDocuments: () => api.get('/dossiers-commerciaux/config/types-documents'),
  createTypeDocument: (data) => api.post('/dossiers-commerciaux/config/types-documents', data),
  updateTypeDocument: (id, data) => api.put(`/dossiers-commerciaux/config/types-documents/${id}`, data),
  deleteTypeDocument: (id) => api.delete(`/dossiers-commerciaux/config/types-documents/${id}`),
  // Les véhicules LIVRÉS, pour le secrétariat. Même ligne que Livraisons, pas
  // une copie : les deux vues ne peuvent pas diverger.
  backOffice: (params) => api.get('/dossiers-commerciaux/back-office', { params }),
  getConditions: (id) => api.get(`/dossiers-commerciaux/config/types-documents/${id}/conditions`),
  addCondition: (id, data) => api.post(`/dossiers-commerciaux/config/types-documents/${id}/conditions`, data),
  deleteCondition: (condId) => api.delete(`/dossiers-commerciaux/config/conditions/${condId}`),
};

// Workflows — modèles de processus + exécutions suivies en temps réel
export const workflowsApi = {
  // Modèles
  listTemplates:     (params)      => api.get('/workflows/templates', { params }),
  getCategories:     ()            => api.get('/workflows/templates/categories'),
  getSocietes:       ()            => api.get('/workflows/templates/societes'),
  getTemplate:       (id)          => api.get(`/workflows/templates/${id}`),
  createTemplate:    (data)        => api.post('/workflows/templates', data),
  updateTemplate:    (id, data)    => api.put(`/workflows/templates/${id}`, data),
  duplicateTemplate: (id, data)    => api.post(`/workflows/templates/${id}/duplicate`, data || {}),
  deleteTemplate:    (id)          => api.delete(`/workflows/templates/${id}`),
  // Exécutions
  createExecution:   (data)        => api.post('/workflows/executions', data),
  listExecutions:    (params)      => api.get('/workflows/executions', { params }),
  myRunning:         ()            => api.get('/workflows/executions/mine/running'),
  getExecution:      (id)          => api.get(`/workflows/executions/${id}`),
  mutateStep:        (id, stepId, action) => api.patch(`/workflows/executions/${id}/steps/${stepId}`, { action }),
  setNote:           (id, stepId, note)   => api.patch(`/workflows/executions/${id}/steps/${stepId}/note`, { note }),
  setCondition:      (id, stepId, checked) => api.patch(`/workflows/executions/${id}/steps/${stepId}/condition`, { condition_checked: checked }),
  answerDecision:    (id, decisionId, answerId) => api.patch(`/workflows/executions/${id}/answer`, { decisionId, answerId }),
  cancelExecution:   (id)          => api.patch(`/workflows/executions/${id}/cancel`),
  deleteExecution:   (id)          => api.delete(`/workflows/executions/${id}`),
  bulkDeleteExecutions: (ids)      => api.post('/workflows/executions/bulk-delete', { ids }),
};

// Documents / Recherche PDF (RAG) — projets documentaires, upload, indexation, chat
export const documentsApi = {
  // Projets
  listProjects:  ()            => api.get('/documents'),
  createProject: (data)        => api.post('/documents', data),
  updateProject: (id, data)    => api.patch(`/documents/${id}`, data),
  deleteProject: (id)          => api.delete(`/documents/${id}`),
  // Documents d'un projet
  listDocuments: (id)          => api.get(`/documents/${id}/documents`),
  presign:       (id, data)    => api.post(`/documents/${id}/documents/presign`, data),
  confirm:       (id, data)    => api.post(`/documents/${id}/documents/confirm`, data),
  uploadFile:    (id, form)    => api.post(`/documents/${id}/documents/upload-file`, form, { headers: { 'Content-Type': 'multipart/form-data' } }),
  fileUrl:       (id, docId)   => api.get(`/documents/${id}/documents/${docId}/file`),
  renameDocument:(id, docId, data) => api.patch(`/documents/${id}/documents/${docId}`, data),
  reindex:       (id, docId)   => api.post(`/documents/${id}/documents/${docId}/reindex`),
  deleteDocument:(id, docId)   => api.delete(`/documents/${id}/documents/${docId}`),
  // Chat
  chat:          (id, question)=> api.post(`/documents/${id}/chat`, { question }),
  conversations: (id)          => api.get(`/documents/${id}/conversations`),
};

// Liasses fiscales (Labo Tools) — extraction structurée par IA + chat
export const liassesApi = {
  // Référentiels
  listTypes:    ()             => api.get('/liasses/types'),
  createType:   (data)         => api.post('/liasses/types', data),
  updateType:   (id, data)     => api.patch(`/liasses/types/${id}`, data),
  deleteType:   (id)           => api.delete(`/liasses/types/${id}`),
  listFields:   (typeId)       => api.get(`/liasses/types/${typeId}/fields`),
  createField:  (typeId, data) => api.post(`/liasses/types/${typeId}/fields`, data),
  updateField:  (fieldId, data)=> api.patch(`/liasses/fields/${fieldId}`, data),
  deleteField:  (fieldId)      => api.delete(`/liasses/fields/${fieldId}`),
  // Dossiers
  listDossiers: ()             => api.get('/liasses/dossiers'),
  createDossier:(data)         => api.post('/liasses/dossiers', data),
  updateDossier:(id, data)     => api.patch(`/liasses/dossiers/${id}`, data),
  deleteDossier:(id)           => api.delete(`/liasses/dossiers/${id}`),
  // Documents
  listDocuments:(id)           => api.get(`/liasses/dossiers/${id}/documents`),
  presign:      (id, data)     => api.post(`/liasses/dossiers/${id}/documents/presign`, data),
  confirm:      (id, data)     => api.post(`/liasses/dossiers/${id}/documents/confirm`, data),
  uploadFile:   (id, form)     => api.post(`/liasses/dossiers/${id}/documents/upload-file`, form, { headers: { 'Content-Type': 'multipart/form-data' } }),
  fileUrl:      (id, docId)    => api.get(`/liasses/dossiers/${id}/documents/${docId}/file`),
  renameDocument:(id, docId, data) => api.patch(`/liasses/dossiers/${id}/documents/${docId}`, data),
  reindex:      (id, docId)    => api.post(`/liasses/dossiers/${id}/documents/${docId}/reindex`),
  deleteDocument:(id, docId)   => api.delete(`/liasses/dossiers/${id}/documents/${docId}`),
  // Analyse + résultats
  analyser:     (id)           => api.post(`/liasses/dossiers/${id}/analyser`),
  resultats:    (id)           => api.get(`/liasses/dossiers/${id}/resultats`),
  updateValue:  (id, data)     => api.patch(`/liasses/dossiers/${id}/values`, data),
  // Chat
  chat:         (id, question) => api.post(`/liasses/dossiers/${id}/chat`, { question }),
  conversations:(id)           => api.get(`/liasses/dossiers/${id}/conversations`),
};

// Contrôle documentaire (Labo Tools) — présence des pièces d'un dossier, vérifiée par IA
export const controlesApi = {
  // Grilles + points
  listGrilles:   ()             => api.get('/controles/grilles'),
  createGrille:  (data)         => api.post('/controles/grilles', data),
  updateGrille:  (id, data)     => api.patch(`/controles/grilles/${id}`, data),
  deleteGrille:  (id)           => api.delete(`/controles/grilles/${id}`),
  listPoints:    (id)           => api.get(`/controles/grilles/${id}/points`),
  createPoint:   (id, data)     => api.post(`/controles/grilles/${id}/points`, data),
  updatePoint:   (pointId, data)=> api.patch(`/controles/points/${pointId}`, data),
  deletePoint:   (pointId)      => api.delete(`/controles/points/${pointId}`),
  // Campagnes
  listCampagnes: ()             => api.get('/controles/campagnes'),
  createCampagne:(data)         => api.post('/controles/campagnes', data),
  deleteCampagne:(id)           => api.delete(`/controles/campagnes/${id}`),
  // Fichiers
  presign:       (id, data)     => api.post(`/controles/campagnes/${id}/fichiers/presign`, data),
  confirm:       (id, data)     => api.post(`/controles/campagnes/${id}/fichiers/confirm`, data),
  upload:        (id, form)     => api.post(`/controles/campagnes/${id}/fichiers/upload`, form, { headers: { 'Content-Type': 'multipart/form-data' } }),
  fileUrl:       (id, docId)    => api.get(`/controles/campagnes/${id}/fichiers/${docId}/url`),
  relancer:      (id, docId)    => api.post(`/controles/campagnes/${id}/fichiers/${docId}/relancer`),
  deleteFichiers:(id, ids)      => api.post(`/controles/campagnes/${id}/fichiers/supprimer`, { ids }),
  // Analyse + résultats
  analyser:      (id)           => api.post(`/controles/campagnes/${id}/analyser`),
  resultats:     (id)           => api.get(`/controles/campagnes/${id}/resultats`),
  corriger:      (id, data)     => api.patch(`/controles/campagnes/${id}/resultats`, data),
  // Liste attendue (rapprochement déterministe fichiers ↔ liste)
  attendus:      (id)           => api.get(`/controles/campagnes/${id}/attendus`),
  setAttendus:   (id, texte)    => api.put(`/controles/campagnes/${id}/attendus`, { texte }),
};

export default api;
